import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import { r2DeletionService } from '../services/r2DeletionService';
import { videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';

interface DeleteVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  videoId: string;
  videoName: string;
  storagePath: string;
  onSuccess: () => void;
  onToast?: (message: string, type: 'success' | 'error') => void;
}

const DeleteVideoModal: React.FC<DeleteVideoModalProps> = ({
  isOpen,
  onClose,
  orgId,
  videoId,
  videoName,
  storagePath,
  onSuccess,
  onToast,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteMaster, setDeleteMaster] = useState(true);
  const [deleteThumbnails, setDeleteThumbnails] = useState(true);
  const [hasThumbnails, setHasThumbnails] = useState(false);
  const [isCheckingThumbnails, setIsCheckingThumbnails] = useState(false);

  // Check if video has thumbnails when modal opens
  useEffect(() => {
    if (isOpen && videoId) {
      checkThumbnails();
    }
  }, [isOpen, videoId]);

  const checkThumbnails = async () => {
    setIsCheckingThumbnails(true);
    try {
      const jobsResult = await videoProcessingJobsService.getJobsByVideoId(videoId);
      if (jobsResult.success && jobsResult.data) {
        const thumbnailJobs = jobsResult.data.filter(
          (job) => job.job_type === 'thumbnail' && job.status === 'succeeded' && job.is_active
        );
        const hasThumbs = thumbnailJobs.length > 0 && thumbnailJobs.some((job) => {
          const generatedAssets = Array.isArray(job.payload?.generatedAssets)
            ? job.payload.generatedAssets
            : [];
          return generatedAssets.length > 0 && generatedAssets.some((asset: any) => asset?.storagePath);
        });
        setHasThumbnails(hasThumbs);
        // If no thumbnails, uncheck the thumbnail option
        if (!hasThumbs) {
          setDeleteThumbnails(false);
        }
      }
    } catch (err) {
      console.error('Error checking thumbnails:', err);
      setHasThumbnails(false);
    } finally {
      setIsCheckingThumbnails(false);
    }
  };

  const handleDelete = async () => {
    // Validation: at least one option must be selected
    if (!deleteMaster && !deleteThumbnails) {
      setError('Please select at least one item to delete');
      return;
    }

    setError(null);
    setIsDeleting(true);

    try {
      const deleteResult = await r2DeletionService.deleteVideoAssets(videoId, {
        deleteMaster,
        deleteThumbnails,
      });

      if (!deleteResult.success) {
        throw new Error(deleteResult.error || 'Failed to delete video assets');
      }

      // Show success toast
      const deletedItems: string[] = [];
      if (deleteMaster) deletedItems.push('master video');
      if (deleteThumbnails) deletedItems.push('thumbnails');

      const successMessage = deletedItems.length === 1
        ? `${deletedItems[0].charAt(0).toUpperCase() + deletedItems[0].slice(1)} deleted successfully`
        : `${deletedItems.join(' and ')} deleted successfully`;

      if (onToast) {
        onToast(successMessage, 'success');
      }

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete video assets';
      setError(errorMessage);
      if (onToast) {
        onToast(errorMessage, 'error');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Video Assets">
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-700">
          Select which assets to delete for <strong>"{videoName}"</strong>:
        </p>
        <p className="text-xs text-gray-500">
          This action cannot be undone. Selected files will be permanently deleted from storage.
        </p>

        {/* Checkboxes */}
        <div className="space-y-3 pt-2">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={deleteMaster}
              onChange={(e) => setDeleteMaster(e.target.checked)}
              disabled={isDeleting}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">Delete Master Video</span>
              <p className="text-xs text-gray-500 mt-0.5">
                The main video file will be permanently deleted from storage
              </p>
            </div>
          </label>

          <label className={`flex items-start space-x-3 ${hasThumbnails ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
            <input
              type="checkbox"
              checked={deleteThumbnails}
              onChange={(e) => setDeleteThumbnails(e.target.checked)}
              disabled={isDeleting || !hasThumbnails || isCheckingThumbnails}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">Delete Thumbnails</span>
              {isCheckingThumbnails ? (
                <p className="text-xs text-gray-500 mt-0.5">Checking for thumbnails...</p>
              ) : hasThumbnails ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  All thumbnail images will be permanently deleted from storage
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-0.5">
                  No thumbnails found for this video
                </p>
              )}
            </div>
          </label>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting || (!deleteMaster && !deleteThumbnails)}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Deleting...' : 'Delete Selected'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteVideoModal;

